import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const PRAYER_NAMES = {
  fajr: 'Fajr',
  dhuhr: 'Dhuhr',
  asr: 'Asr',
  maghrib: 'Maghrib',
  isha: 'Isha',
};

const PRAYER_ICONS = {
  fajr: '🌙',
  dhuhr: '☀️',
  asr: '🌤',
  maghrib: '🌇',
  isha: '🌃',
};

const GRACE_PERIOD_MS = 10 * 60 * 1000; // 10 minutes

function parseTime(timeStr, baseDate) {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const d = new Date(baseDate);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

function getPrayerState(prayerTimes) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

  // Build list with times
  const list = prayers
    .map((p) => ({ name: p, time: parseTime(prayerTimes[p], today) }))
    .filter((p) => p.time);

  // Check if we're in a grace period (prayer started within last 10 mins)
  const passed = list.filter((p) => p.time <= now);
  if (passed.length > 0) {
    const last = passed[passed.length - 1];
    const elapsed = now - last.time;
    if (elapsed <= GRACE_PERIOD_MS) {
      const remaining = GRACE_PERIOD_MS - elapsed;
      return { mode: 'grace', current: last, remaining };
    }
  }

  // Find next upcoming prayer
  const next = list.find((p) => p.time > now);
  if (next) return { mode: 'countdown', next };

  // All passed — next is fajr tomorrow
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return { mode: 'countdown', next: { name: 'fajr', time: parseTime(prayerTimes.fajr, tomorrow) } };
}

export default function CountdownTimer({ prayerTimes }) {
  const [state, setState] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (!prayerTimes) return;

    const tick = () => {
      const now = new Date();
      setCurrentTime(now);
      const s = getPrayerState(prayerTimes);
      setState(s);

      if (s.mode === 'countdown' && s.next?.time) {
        const diff = s.next.time - now;
        if (diff > 0) {
          setCountdown({
            hours: Math.floor(diff / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((diff % (1000 * 60)) / 1000),
          });
        }
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [prayerTimes]);

  const pad = (n) => String(n).padStart(2, '0');

  if (!prayerTimes || !state) {
    return (
      <div className="flex items-center gap-3 text-gray-400">
        <Clock className="w-5 h-5 animate-pulse" />
        <span className="text-sm">Loading prayer times...</span>
      </div>
    );
  }

  // ── Grace period — "It's Asr time now!" ──────────────────────────────
  if (state.mode === 'grace') {
    const mins = Math.floor(state.remaining / 60000);
    const secs = Math.floor((state.remaining % 60000) / 1000);
    return (
      <div className="text-center">
        <div className="text-gray-400 text-sm mb-4">
          {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
        <motion.div
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="mb-4"
        >
          <div className="text-4xl mb-2">{PRAYER_ICONS[state.current.name]}</div>
          <div className="text-2xl font-bold text-emerald-400">
            It's {PRAYER_NAMES[state.current.name]} time now!
          </div>
          <p className="text-gray-400 text-sm mt-1">
            Started at {state.current.time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </motion.div>
        <div className="flex items-center justify-center gap-2 mt-3">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-gray-500 text-xs">
            Next prayer in {pad(mins)}:{pad(secs)}
          </span>
        </div>
      </div>
    );
  }

  // ── Countdown to next prayer ──────────────────────────────────────────
  return (
    <div className="text-center">
      <div className="text-gray-400 text-sm mb-2">
        {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>

      <div className="mb-4">
        <span className="badge-gold text-base px-4 py-1 inline-block">
          Next: {PRAYER_NAMES[state.next.name]}
        </span>
        {state.next.time && (
          <p className="text-gray-400 text-sm mt-1">
            at {state.next.time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>

      <div className="flex items-center justify-center gap-3">
        {[
          { value: countdown.hours, label: 'hrs' },
          { value: countdown.minutes, label: 'min' },
          { value: countdown.seconds, label: 'sec' },
        ].map(({ value, label }, i) => (
          <React.Fragment key={label}>
            {i > 0 && <span className="text-emerald-400 font-bold text-xl mb-4">:</span>}
            <motion.div
              key={`${label}-${value}`}
              className="countdown-digit"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-3xl font-bold text-white font-mono">{pad(value)}</div>
              <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{label}</div>
            </motion.div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
